import { useEffect, useState } from 'react';

/**
 * El valor de una promesa cuando resuelve, y `undefined` mientras no.
 *
 * ── Para qué existe ────────────────────────────────────────────────────────
 *
 * El detalle del recorrido de vigilancia entrega DOS promesas: el snapshot de
 * la caché, que el `<Await>` resuelve al instante, y la carga del servidor,
 * que llega cuando llega. La pantalla pinta `fresco ?? snapshot`, así que hace
 * falta leer la segunda sin bloquear el render de la primera.
 *
 * ── Reglas ─────────────────────────────────────────────────────────────────
 *
 *  - `null` es un VALOR («el servidor dice que no hay»), no «todavía no». Por
 *    eso el estado no se representa con `undefined | T` a secas, sino con la
 *    promesa que lo produjo.
 *  - Una promesa NUEVA olvida el valor de la anterior. Cada ejecución del
 *    loader crea una: sin este olvido, al cambiar de recorrido se quedaría en
 *    pantalla el dato del anterior hasta que respondiera el nuevo.
 *  - Una promesa vieja que resuelve tarde no pisa a la nueva.
 *  - Un rechazo se queda en `undefined`: quién decide qué hacer con el fallo
 *    es el loader, no esto.
 */
export function useResolvedPromise<T>(promise: Promise<T> | undefined): T | undefined {
  const [settled, setSettled] = useState<{
    promise: Promise<T> | undefined;
    value: T | undefined;
  }>({ promise: undefined, value: undefined });

  useEffect(() => {
    if (!promise) return undefined;
    let cancelled = false;
    promise
      .then((value) => {
        if (!cancelled) setSettled({ promise, value });
      })
      .catch(() => {
        // Sin efectos a propósito: el loader ya decide qué significa un fallo.
      });
    return () => {
      cancelled = true;
    };
  }, [promise]);

  // Comparación en el RENDER, no en un efecto: el valor viejo no puede llegar
  // a pintarse ni un solo fotograma junto a una promesa que ya no es la suya.
  return settled.promise === promise ? settled.value : undefined;
}

export default useResolvedPromise;
